/** Hides mobile chrome during scrolling; a small gesture after a pause reveals it. */
class SMobileScrollChrome extends HTMLElement {
  #abort;
  #resizeObserver;
  #header;
  #nav;
  #mobile;
  #lastY = 0;
  #travel = 0;
  #settled = true;
  #settleTimer;
  #lastScrollAt = 0;

  connectedCallback() {
    this.#header = document.querySelector('[data-mobile-scroll-header]');
    this.#nav = document.querySelector('[data-mobile-scroll-nav]');
    if (!this.#header || !this.#nav) return;

    this.#mobile = window.matchMedia('(max-width: 767px)');
    this.#abort = new AbortController();
    const signal = this.#abort.signal;
    this.#resizeObserver = new ResizeObserver(() => {
      this.#header.style.setProperty('--mobile-header-height', `${this.#header.offsetHeight}px`);
    });
    this.#resizeObserver.observe(this.#header);
    this.#header.style.setProperty('--mobile-header-height', `${this.#header.offsetHeight}px`);
    this.#lastY = Math.max(0, window.scrollY);

    window.addEventListener('scroll', this.#onScroll, { passive: true, signal });
    window.addEventListener('pointerdown', this.#onTouchStart, { passive: true, signal });
    this.#mobile.addEventListener('change', this.#reset, { signal });
    window.addEventListener('focusin', this.#onFocus, { signal });
    window.addEventListener('keydown', this.#onKeydown, { signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#resizeObserver?.disconnect();
    clearTimeout(this.#settleTimer);
    this.#setHidden(false);
  }

  #reset = () => {
    clearTimeout(this.#settleTimer);
    this.#lastY = Math.max(0, window.scrollY);
    this.#travel = 0;
    this.#settled = true;
    this.#setHidden(false);
  };

  #onFocus = (event) => {
    if (this.#header.contains(event.target) || this.#nav.contains(event.target)) this.#setHidden(false);
  };

  #onKeydown = (event) => {
    // Restore both controls before keyboard focus can enter either off-screen region.
    if (event.key === 'Tab') this.#setHidden(false);
  };

  #onTouchStart = (event) => {
    // A fresh touch after a brief pause can reveal the bars on its first tiny scroll.
    if (event.pointerType !== 'touch' || !this.#mobile.matches) return;
    if (Date.now() - this.#lastScrollAt < 80) return;
    clearTimeout(this.#settleTimer);
    this.#settled = true;
    this.#travel = 0;
  };

  #onScroll = () => {
    const y = Math.max(0, window.scrollY);
    const distance = Math.abs(y - this.#lastY);
    this.#lastY = y;
    this.#lastScrollAt = Date.now();

    if (!this.#mobile.matches || y <= 20 || document.querySelector('dialog[open]')) {
      this.#reset();
      return;
    }
    if (distance < 1) return;

    clearTimeout(this.#settleTimer);
    this.#settleTimer = setTimeout(() => {
      this.#settled = true;
      this.#travel = 0;
    }, 140);

    const focusedControl = document.activeElement;
    const keepVisible = focusedControl?.matches('input, textarea, select, :focus-visible') &&
      (this.#header.contains(focusedControl) || this.#nav.contains(focusedControl));
    if (keepVisible) {
      this.#setHidden(false);
      this.#travel = 0;
      this.#settled = false;
      return;
    }

    // Only a short new gesture after scrolling stops reveals the controls.
    if (this.#settled) {
      this.#travel = 0;
      if (this.#nav.hasAttribute('data-scroll-hidden') && distance <= 24) this.#setHidden(false);
    }
    this.#settled = false;
    this.#travel += distance;
    if (y > 72 && this.#travel >= 32) this.#setHidden(true);
  };

  #setHidden(hidden) {
    if (!this.#header || !this.#nav) return;
    this.#header.toggleAttribute('data-scroll-hidden', hidden);
    this.#nav.toggleAttribute('data-scroll-hidden', hidden);
    this.#nav.inert = hidden;
    // Floating controls such as the WhatsApp button hide and reveal together with the bars.
    for (const companion of document.querySelectorAll('[data-mobile-scroll-companion]')) {
      companion.toggleAttribute('data-scroll-hidden', hidden);
      companion.inert = hidden;
    }
  }
}

customElements.define('s-mobile-scroll-chrome', SMobileScrollChrome);
